class FalconVividVideo extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedToLifecycle) return;

    this.isConnectedToLifecycle = true;
    this.blocks = Array.from(this.querySelectorAll('.video-block'));
    this.progress = document.getElementById(this.dataset.progressId);
    this.mobileQuery = window.matchMedia('(max-width: 768px)');
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.lifecycleController = new AbortController();
    this.sectionVisible = false;
    this.scrollFrame = null;

    if (!this.blocks.length) return;
    if (!this.blocks.some((block) => block.classList.contains('is-active'))) {
      this.blocks[0].classList.add('is-active');
    }

    this.sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.sectionVisible = entry?.isIntersecting ?? false;
        this.syncPlayback();
      },
      { rootMargin: '120px 0px', threshold: 0.01 }
    );
    this.sectionObserver.observe(this);

    this.onVisibilityChange = () => this.syncPlayback();
    this.onModeChange = () => this.setupMode();
    document.addEventListener('visibilitychange', this.onVisibilityChange, {
      signal: this.lifecycleController.signal,
    });
    this.mobileQuery.addEventListener('change', this.onModeChange, {
      signal: this.lifecycleController.signal,
    });
    this.motionQuery.addEventListener('change', this.onVisibilityChange, {
      signal: this.lifecycleController.signal,
    });
    this.soundOn = false;
    this.blocks.forEach((block) => {
      block.querySelector('.vivid-sound-toggle')?.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!block.classList.contains('is-active')) {
          this.activate(block, false);
          return;
        }
        this.setSound(!this.soundOn);
      }, {
        signal: this.lifecycleController.signal,
      });
      block.addEventListener('click', () => this.activate(block, false), {
        signal: this.lifecycleController.signal,
      });
      block.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.activate(block, false);
      }, {
        signal: this.lifecycleController.signal,
      });
    });

    this.setupMode();
  }

  setupMode() {
    this.modeController?.abort();
    this.modeController = new AbortController();

    if (this.mobileQuery.matches) {
      this.addEventListener('scroll', () => this.scheduleMobileUpdate(), {
        passive: true,
        signal: this.modeController.signal,
      });
      this.scheduleMobileUpdate();
      return;
    }

    this.blocks.forEach((block) => {
      block.addEventListener('pointerenter', () => this.activate(block, true), {
        signal: this.modeController.signal,
      });
    });
    this.syncPlayback();
  }

  scheduleMobileUpdate() {
    if (this.scrollFrame !== null) return;

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateMobileState();
    });
  }

  updateMobileState() {
    if (!this.blocks.length) return;

    const center = this.scrollLeft + this.clientWidth / 2;
    const activeBlock = this.blocks.reduce((closest, block) => {
      const blockCenter = block.offsetLeft + block.offsetWidth / 2;
      const closestCenter = closest.offsetLeft + closest.offsetWidth / 2;
      return Math.abs(blockCenter - center) < Math.abs(closestCenter - center) ? block : closest;
    }, this.blocks[0]);

    this.activate(activeBlock, false);
    this.updateProgress(activeBlock);
  }

  updateProgress(activeBlock) {
    if (!this.progress) return;

    const fill = this.progress.querySelector('.vivid-carousel-progress-fill');
    const counter = this.progress.querySelector('.vivid-carousel-progress-counter');
    const maxScroll = Math.max(this.scrollWidth - this.clientWidth, 0);
    const scrollRatio = maxScroll > 0 ? Math.min(Math.max(this.scrollLeft / maxScroll, 0), 1) : 0;
    const viewportRatio = this.scrollWidth > 0 ? Math.min(this.clientWidth / this.scrollWidth, 1) : 1;
    const activeIndex = Math.max(this.blocks.indexOf(activeBlock), 0);

    if (fill) {
      fill.style.width = `${viewportRatio * 100}%`;
      fill.style.left = `${scrollRatio * (1 - viewportRatio) * 100}%`;
    }
    if (counter) counter.textContent = `${activeIndex + 1} / ${this.blocks.length}`;
    this.progress.setAttribute('aria-valuenow', String(Math.round(scrollRatio * 100)));
  }

  activate(activeBlock, resetInactive) {
    if (!activeBlock) return;

    this.blocks.forEach((block) => {
      const video = block.querySelector('video');
      const isActive = block === activeBlock;

      block.classList.toggle('is-active', isActive);
      block.setAttribute('aria-pressed', String(isActive));
      if (video) video.muted = !(isActive && this.soundOn);
      if (!isActive && video) {
        video.pause();
        if (resetInactive && video.currentTime) video.currentTime = 0;
      }
    });
    this.syncPlayback();
  }

  setSound(on) {
    this.soundOn = on;
    this.blocks.forEach((block) => {
      const video = block.querySelector('video');
      const toggle = block.querySelector('.vivid-sound-toggle');
      const isActive = block.classList.contains('is-active');

      if (video) video.muted = !(on && isActive);
      if (toggle) {
        toggle.classList.toggle('is-unmuted', on);
        toggle.setAttribute('aria-pressed', String(on));
        toggle.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
      }
    });
    this.syncPlayback();
  }

  syncPlayback() {
    const canPlay = this.sectionVisible && !document.hidden && !this.motionQuery.matches;

    this.blocks?.forEach((block) => {
      const video = block.querySelector('video');
      if (!video) return;

      if (!canPlay || !block.classList.contains('is-active')) {
        video.pause();
        return;
      }

      video.preload = 'metadata';
      video.play().catch(() => {
        // Browser refused unmuted playback: fall back to muted so the video keeps playing.
        if (!video.muted) {
          this.setSound(false);
        }
      });
    });
  }

  disconnectedCallback() {
    this.lifecycleController?.abort();
    this.modeController?.abort();
    this.sectionObserver?.disconnect();
    if (this.scrollFrame !== null) cancelAnimationFrame(this.scrollFrame);
    this.blocks?.forEach((block) => block.querySelector('video')?.pause());

    this.isConnectedToLifecycle = false;
    this.scrollFrame = null;
  }
}

if (!customElements.get('falcon-vivid-video')) {
  customElements.define('falcon-vivid-video', FalconVividVideo);
}
